import React, { useCallback, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { FormikHelpers, FormikValues } from "formik";
import { useHistory } from "react-router-dom";

import PageLayout from "../../_layout/PageLayout";
import Form from "../../../components/form/Form";
import { DataSection, SubSection } from "../../../components/data-components/sections/DataSections";
import { translationService } from "../../../services/translation/TranslationService";
import DisplayDescription from "../../../components/data-components/DisplayDescription";
import dateService from "../../../services/date-service/DateService";
import { OrganizationRequestInput } from "../../../api-models/request/OrganizationRequestInput";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import { currentUserService } from "../../../services/user/CurrentUserStorage";
import { MyAccountMenuItemsPages, MyAccountMenuItemsModel } from "../MyAccountMenuItems";
import { OrganizationRequestsStorageModel } from "../../../services/organization/OrganizationRequestsStorage";
import { api } from "../../../services/api/ApiService";
import {
  EditMessageSection,
  EditTextField,
  EditDropdownField,
  EditDropdownSuggestionField,
  DisplayTextField,
} from "./DataComponents";
import DisplayDateCreated from "../../../components/data-components/DisplayDateCreated";
import { AvailableCountriesStorageModel } from "../../../services/organization/AvailableCountriesStorage";
import {
  AddressCleansingServiceModel,
  AddressEntity,
} from "../../../services/address/AddressCleansingService";
import { TermsAndConditions } from "../../../components/terms-and-conditions/TermsAndConditions";
import "../composition.scss";
import { warningLeavePageService } from "../../../services/page/WarningLeavePageService";

export default observer(() => {
  const history = useHistory();
  const { handleErrorInSnackbar } = useErrorHandler();

  const [availableCountriesStorage] = useState(() =>
    AvailableCountriesStorageModel.create({}, { api })
  );

  const [addressCleansingService] = useState(() =>
    AddressCleansingServiceModel.create({}, { api })
  );

  const [organizationRequestsStorage] = useState(() =>
    OrganizationRequestsStorageModel.create({ autoLoad: false }, { api })
  );

  const menuItems = useMemo(() => {
    const myAccountMenuItems = MyAccountMenuItemsModel.create({
      menuItemPage: MyAccountMenuItemsPages.RequestOrganization,
    });

    return {
      staticItems: myAccountMenuItems.getStaticItems(),
    };
  }, []);

  const description = useMemo(
    () => translationService.translate("RequestOrganization.description"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translationService.translate("RequestOrganization.description")]
  );

  const availableCountries = useMemo(() => {
    return availableCountriesStorage.getAvailableCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCountriesStorage.isLoading, translationService.language]);

  const initialValues = useMemo<OrganizationRequestInput>(() => {
    return {
      name: "",
      country: "DE",
      state: "",
      city: "",
      zip: "",
      address: "",
      addressNumber: "",
      vat: "",
      generalManagerFirstName: "",
      generalManagerLastName: "",
      phoneNumber: "",
      message: "",
      websiteURL: "",
      contactPersonEmail: undefined,
    };
  }, []);

  const goToOrganizationRequestsList = useCallback(
    () => history.push("/my-account/organizations/organization-requests"),
    [history]
  );

  const handleSubmit = useCallback(
    async (values: FormikValues, formActions: FormikHelpers<FormikValues>) => {
      const organizationRequestInput: OrganizationRequestInput = {
        name: values.name,
        country: values.country,
        state: values.state,
        city: values.city,
        zip: values.zip,
        address: values.address,
        addressNumber: values.addressNumber,
        vat: values.vat,
        generalManagerFirstName: values.generalManagerFirstName,
        generalManagerLastName: values.generalManagerLastName,
        phoneNumber: values.phoneNumber,
        message: values.message,
        websiteURL: values.websiteURL,
        invitedClientEmail: values.invitedClientEmail,
      };
      try {
        await organizationRequestsStorage.requestNewOrganization(organizationRequestInput);
        warningLeavePageService.setIsDirty(false);
        return goToOrganizationRequestsList();
      } catch (error) {
        const applicationError = error as ApplicationError;
        applicationError.withForm(values, formActions);
        applicationError.withErrorMessage("default-error.organization.request");
        handleErrorInSnackbar(applicationError);
      }
    },
    [goToOrganizationRequestsList, handleErrorInSnackbar, organizationRequestsStorage]
  );

  return (
    <PageLayout
      titleKey="RequestOrganization.request"
      id="request-organization"
      menuItems={menuItems}
      isLoading={availableCountriesStorage.isLoading}
    >
      <Form
        formikOptions={{
          initialValues: initialValues,
          onSubmit: handleSubmit,
          validateOnBlur: false,
          validateOnChange: false,
        }}
        saveButtonText="general.send.request"
        onCancel={goToOrganizationRequestsList}
      >
        {({ values, setFieldValue }) => {
          return (
            <>
              <DataSection labelKey="RequestOrganization.request">
                <div className="request-organization-description-container">
                  <DisplayDescription description={description} />
                  <TermsAndConditions
                    textKeyBeforeLinks="RequestNewOrganizationButton.terms-and-conditions-acceptance-part-1"
                    textKeyAfterLinks="RequestNewOrganizationButton.terms-and-conditions-acceptance-part-2"
                  />
                </div>
              </DataSection>
              <div className="request-organization-form-container">
                <div className="organization-details-container">
                  <DataSection labelKey="general.organization.details">
                    <SubSection hideRightBorder>
                      <EditTextField
                        id="organization-name"
                        labelKey="general.organization.name"
                        placeholderKey="organization.EditOrganizationName.placeholder"
                        fieldName="name"
                        name={values.name}
                        required
                      />
                      <EditDropdownField
                        id="organization-country"
                        labelKey="general.organization.country"
                        fieldName="country"
                        name={values.country}
                        required
                        options={availableCountries}
                        setSelectedItem={(item) => {
                          setFieldValue("country", item ?? "");
                          setFieldValue("address", "");
                          setFieldValue("zip", "");
                          setFieldValue("city", "");
                          setFieldValue("state", "");
                        }}
                      />
                      <EditDropdownSuggestionField
                        id="organization-address"
                        labelKey="general.organization.address"
                        placeholderKey="organization.EditOrganizationAddress.placeholder"
                        fieldName="address"
                        fieldValue={values.address}
                        required
                        performSearch={(query) =>
                          addressCleansingService.search(values.country, query)
                        }
                        searchTextChanged={(query) => (values.address = query)}
                        selectedItemChanged={(item: AddressEntity) => {
                          setFieldValue("address", item.street);
                          setFieldValue("zip", item.zip);
                          setFieldValue("city", item.city);
                          setFieldValue("state", item.state);
                        }}
                        clearCurrentItem={() => setFieldValue("address", "")}
                      />
                      <EditTextField
                        id="organization-address-number"
                        labelKey="general.organization.address-number"
                        placeholderKey="organization.EditOrganizationAddressNumber.placeholder"
                        fieldName="addressNumber"
                        name={values.addressNumber}
                        required
                      />
                      <EditDropdownSuggestionField
                        id="organization-zip"
                        labelKey="general.organization.zip"
                        placeholderKey="organization.EditOrganizationZip.placeholder"
                        fieldName="zip"
                        fieldValue={values.zip}
                        required
                        performSearch={(query) =>
                          addressCleansingService.search(values.country, query)
                        }
                        searchTextChanged={(query) => (values.zip = query)}
                        selectedItemChanged={(item: AddressEntity) => {
                          setFieldValue("address", item.street);
                          setFieldValue("zip", item.zip);
                          setFieldValue("city", item.city);
                          setFieldValue("state", item.state);
                        }}
                        clearCurrentItem={() => setFieldValue("zip", "")}
                      />
                      <EditDropdownSuggestionField
                        id="organization-city"
                        labelKey="general.organization.city"
                        placeholderKey="organization.EditOrganizationCity.placeholder"
                        fieldName="city"
                        fieldValue={values.city}
                        required
                        performSearch={(query) =>
                          addressCleansingService.search(values.country, query)
                        }
                        searchTextChanged={(query) => (values.city = query)}
                        selectedItemChanged={(item: AddressEntity) => {
                          setFieldValue("address", item.street);
                          setFieldValue("zip", item.zip);
                          setFieldValue("city", item.city);
                          setFieldValue("state", item.state);
                        }}
                        clearCurrentItem={() => setFieldValue("city", "")}
                      />
                      <EditTextField
                        id="organization-state"
                        labelKey="general.organization.state"
                        placeholderKey="organization.EditOrganizationState.placeholder"
                        fieldName="state"
                        name={values.state}
                      />
                      <EditTextField
                        id="organization-vatNumber"
                        labelKey="general.organization.vat"
                        placeholderKey="organization.EditOrganizationVAT.placeholder"
                        fieldName="vat"
                        name={values.vat}
                      />
                    </SubSection>
                    <SubSection>
                      <EditTextField
                        id="general-manager-first-name"
                        labelKey="general.general-manager-first-name"
                        placeholderKey="general.general-manager-first-name-placeholder"
                        fieldName="generalManagerFirstName"
                        name={values.generalManagerFirstName}
                        required
                      />
                      <EditTextField
                        id="general-manager-last-name"
                        labelKey="general.general-manager-last-name"
                        placeholderKey="general.general-manager-last-name-placeholder"
                        fieldName="generalManagerLastName"
                        name={values.generalManagerLastName}
                        required
                      />
                      <EditTextField
                        id="user-phoneNumber"
                        labelKey="organization.organization-phone-number"
                        placeholderKey="organization.organization-phone-number-placeholder"
                        fieldName="phoneNumber"
                        name={values.phoneNumber}
                        required
                      />
                      <EditTextField
                        id="website-url"
                        labelKey="general.website-url"
                        placeholderKey="organization.EditOrganizationWebsiteURL.placeholder"
                        fieldName="websiteURL"
                        name={values.websiteURL}
                      />
                      {currentUserService.currentUser.isSupportAccount && (
                        <EditTextField
                          id="general.invited-client-email"
                          labelKey="general.invited-client-email"
                          placeholderKey="general.invited-client-email-placeholder"
                          fieldName="invitedClientEmail"
                          name={values.invitedClientEmail}
                          isEmail
                        />
                      )}
                    </SubSection>
                  </DataSection>
                </div>
                <div className="requested-by-container">
                  <DataSection labelKey="general.requested-by">
                    <SubSection>
                      <DisplayTextField
                        id="user-username"
                        labelKey="general.user-name"
                        value={currentUserService.currentUser.userName}
                      />
                      <DisplayTextField
                        id="organization-requestedBy"
                        labelKey="general.requested-by"
                        value={currentUserService.currentUser.email}
                      />
                      <DisplayDateCreated date={dateService.currentDate} />
                    </SubSection>
                  </DataSection>
                </div>
              </div>
              <EditMessageSection
                message={values.message}
                placeholderKey="RequestOrganization.message.placeholder"
              />
            </>
          );
        }}
      </Form>
    </PageLayout>
  );
});
